import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { RouteSectionSlots } from './section.types';

/**
 * Sorts a route section's children into the places the section draws them. Order within a slot is
 * the configured order; a child the section does not recognise goes to `rest`, never away.
 *
 * Exactly one route is the stop list: the first child of type `route`, whatever slot it names. A
 * second route goes to `rest` and keeps its own header and add button (RouteSectionSlot.owns).
 */
export function partitionRouteSection(children: FormlyFieldConfig[] | undefined): RouteSectionSlots {
  const slots: RouteSectionSlots = { departure: [], stops: [], destination: [], footer: [], rest: [] };
  for (const child of children ?? []) {
    const slot = child.props?.['routeSlot'];
    if (child.type === 'route') {
      (slots.stops.length ? slots.rest : slots.stops).push(child);
    } else if (slot === 'departure' || slot === 'destination' || slot === 'footer') {
      slots[slot].push(child);
    } else {
      slots.rest.push(child);
    }
  }
  return slots;
}

interface MapLocation {
  location?: { entry?: { geolocation?: Coordinates; address?: Coordinates } };
}
interface Coordinates {
  latitude?: unknown;
  longitude?: unknown;
}

/**
 * Whether the map could draw this route location. Mirrors the extraction in route-map.component.ts:
 * `entry.geolocation`, or `entry.address` when that carries a latitude. An empty stop is still a
 * route-item, but has nothing to draw.
 */
export function hasMapCoordinates(location: unknown): boolean {
  const entry = (location as MapLocation | null)?.location?.entry;
  const geo = entry?.address?.latitude != null ? entry.address : entry?.geolocation;
  return !!geo?.latitude && !!geo?.longitude;
}
